import { IS_INSET_DESIGN_ENABLED } from '@/app-shell/flags/shell-flags';
import { BrandIconStackedSvg } from '@/assets/logos/icon-stacked-svg';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated';
// migration-allow(composition-import): the community page renders the logged-out title host that supplies localized countries
import { LoggedOutStateTitleHost } from '@/composition/hosts/templates/logged-out-state-title-host';
import { useUrlFilters } from '@/hooks/useUrlFilters';
import { cn } from '@/lib/cn';
import { Button } from '@heidi/design-system/button';
import { PageShell } from '@heidi/design-system/page-shell';
import { SearchInput } from '@heidi/design-system/search-input';
import { TypographyH1 } from '@heidi/design-system/typography';
import { Pagination } from '@heidi/snowflake-ui/pagination';
import {
  TemplatesEmpty,
  communityMessages,
  useQueryCommunityTemplates,
} from '@heidi/templates';
import { FilterSettings } from '@heidi/templates/contracts';
import { communityTemplatePageSize } from '@heidi/templates/data';
import { ArrowLeft, Loader2 } from 'lucide-react';
import Head from 'next/head';
import { useRouter } from 'next/router';
import { useEffect, useMemo, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useDebouncedCallback } from 'use-debounce';
import Filters from './filters';
import RelatedTemplates from './relatedTemplates';
import TemplateCards from './templateCards';
const noSpecialtySettings = [
  String(FilterSettings.ALL),
  String(FilterSettings.INITIAL),
  String(FilterSettings.UNDEFINED),
];
// Keeps the search responsive while typing without spamming the URL/history on
// every keystroke. Matches the value used by the sidebar session search.
const SEARCH_DEBOUNCE_MS = 250;

const Community = () => {
  const intl = useIntl();
  const router = useRouter();
  const { isAuthenticated: authed } = useIsAuthenticated();

  const {
    page,
    setPage,
    sort,
    location,
    setLocation,
    specialty,
    setSpecialty,
    category,
    search,
    setSearch,
    resetFilters,
    isReady,
    specialties,
  } = useUrlFilters();
  // Local state so typing stays snappy; the URL is updated via a debounced
  // effect so we don't write to history on every keystroke.
  const [searchInput, setSearchInput] = useState(search);
  // True from the moment a search is submitted until its results land. Keeps the
  // in-input spinner tied to search only — not sort/pagination/filter changes or
  // background refetches, which all also flip isFetching.
  const [isSearchPending, setIsSearchPending] = useState(false);

  const {
    data,
    isLoading: isQueryLoading,
    isFetching,
  } = useQueryCommunityTemplates({
    page,
    sort,
    location,
    specialty,
    category,
    search,
    specialties,
    enabled: isReady,
  });

  // Show loading state until router has parsed URL params AND query has loaded
  const isLoading = !isReady || isQueryLoading;

  // Clear the pending flag once fresh results land (data ref changes on every
  // settled fetch), so the spinner doesn't linger or leak onto later refetches.
  useEffect(() => {
    setIsSearchPending(false);
  }, [data]);

  // Spinner shows only while a submitted search is in flight.
  const isSearching = isSearchPending && isFetching;

  // Result-state predicates derived once so the JSX below stays readable.
  const hasSettledZeroResults = !isLoading && data?.total === 0;
  const recommendedCount = data?.recommended_templates?.length ?? 0;
  const hasZeroResultsWithRecommendations = hasSettledZeroResults && recommendedCount > 0;
  const hasZeroResultsWithoutRecommendations =
    hasSettledZeroResults && recommendedCount === 0;

  const specialtyDisplay = useMemo(() => {
    const shouldNotShowSpecialty = !specialty || noSpecialtySettings.includes(specialty);
    if (shouldNotShowSpecialty) {
      return intl.formatMessage(communityMessages.defaultSpecialty);
    }
    return specialty;
  }, [intl, specialty]);

  const handleClick = () => {
    // For a text-search miss, the search query itself is the most-constraining
    // thing — relaxing specialty/location wouldn't recover results. Drop the
    // search and let any remaining filters decide the result set.
    if (search) {
      handleClearSearch();
      return;
    }
    if (
      data?.recommended_templates.every((template) => template.specialty === specialty)
    ) {
      setLocation(FilterSettings.ALL);
    } else if (
      data?.recommended_templates.every((template) => template.country === location)
    ) {
      setSpecialty(FilterSettings.ALL);
    } else {
      setSpecialty(FilterSettings.ALL);
      setLocation(FilterSettings.ALL);
    }
  };

  // Tracks the value we most recently handed to `setSearch` so the URL→input
  // sync effect below can tell "the router is still catching up to our write"
  // (skip) from "search just changed externally" (sync). Cleared once the
  // router reflects the value. `null` means no commit is in flight.
  const pendingCommitRef = useRef<string | null>(null);

  // Push the typed value into the URL after a short pause so the user sees
  // results as they type without every keystroke writing to the URL bar.
  const debouncedSetSearch = useDebouncedCallback((value: string) => {
    pendingCommitRef.current = value;
    setSearch(value);
    setIsSearchPending(true);
  }, SEARCH_DEBOUNCE_MS);

  const commitSearchNow = (value: string) => {
    debouncedSetSearch.cancel();
    if (value !== search) {
      pendingCommitRef.current = value;
      setSearch(value);
      setIsSearchPending(true);
    }
  };

  const handleSearchChange = (value: string) => {
    setSearchInput(value);
    if (value === search) {
      // Typing brought the input back to the current applied value (e.g.
      // backspaced edits). Cancel any pending update so we don't re-fire it.
      debouncedSetSearch.cancel();
      return;
    }
    debouncedSetSearch(value);
  };

  const handleClearSearch = () => {
    setSearchInput('');
    commitSearchNow('');
  };

  // Sync the URL search value into the input whenever they diverge and the
  // input isn't the source of truth. Covers three cases with one effect:
  //   1. Hydration — initial useState(search) captures the empty default
  //      because the router parses URL params asynchronously; once isReady
  //      flips true, search becomes the real URL value and we sync.
  //   2. Same-route history nav (Pages Router re-renders rather than
  //      remounting on back/forward) — restored search value gets mirrored
  //      back into the input.
  //   3. External clear / programmatic setSearch.
  // Two skip conditions guard the input:
  //   - A local commit is still propagating through the async router. Without
  //     this we'd briefly clobber a just-cleared input with the stale URL
  //     value before router.push resolves (visible flicker on clear / Enter).
  //   - A debounce is queued (user actively typing). Input is the source of
  //     truth until the debounce fires.
  useEffect(() => {
    if (pendingCommitRef.current !== null) {
      if (search === pendingCommitRef.current) {
        pendingCommitRef.current = null;
      }
      return;
    }
    if (searchInput !== search && !debouncedSetSearch.isPending()) {
      setSearchInput(search);
    }
  }, [debouncedSetSearch, search, searchInput]);

  // Cancel any pending debounced URL write on unmount so we don't fire
  // setSearch / setIsSearchPending against an unmounted page when the user
  // navigates away within the debounce window.
  useEffect(() => () => debouncedSetSearch.cancel(), [debouncedSetSearch]);

  const clearSearchLabel = intl.formatMessage(communityMessages.clearSearch);
  const pageTitle = intl.formatMessage(communityMessages.pageTitle, {
    specialty: specialtyDisplay,
  });
  const metaDescription = intl.formatMessage(communityMessages.metaDescription, {
    specialty: specialtyDisplay,
  });
  const logoAlt = intl.formatMessage(communityMessages.logoAlt);
  const searchPlaceholder = intl.formatMessage(communityMessages.searchPlaceholder);

  return (
    <>
      <Head>
        <title>{pageTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:type" content="website" />
      </Head>

      <div
        className={
          authed
            ? `h-full overflow-auto ${IS_INSET_DESIGN_ENABLED() ? 'sm:bg-surface-shell' : 'sm:bg-surface-page'}`
            : 'flex h-dvh w-full justify-center overflow-auto bg-surface'
        }
      >
        <PageShell.Container
          className={cn(
            'flex flex-col items-center gap-8 py-8 sm:gap-16',
            !authed && 'h-fit min-h-full',
            hasZeroResultsWithRecommendations && 'sm:gap-8'
          )}
        >
          <div className="flex w-full flex-col items-center gap-10 sm:gap-16">
            <div className="flex w-full items-center justify-between gap-2">
              <Button
                variant="ghost"
                size="sm"
                flush="start"
                className={cn('w-fit', !authed && 'hidden')}
                onClick={() => void router.push('/scribe/my-templates')}
              >
                <ArrowLeft className="rtl:-scale-x-100" />
                <span className="sr-only md:not-sr-only">
                  <FormattedMessage {...communityMessages.myTemplates} />
                </span>
              </Button>

              <a
                target="_self"
                href="https://heidihealth.com"
                className={cn(authed && 'hidden')}
              >
                <BrandIconStackedSvg height={27} alt={logoAlt} className="max-h-[27px]" />
              </a>

              <SearchInput
                onChange={(e) => handleSearchChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    commitSearchNow(searchInput);
                  }
                }}
                value={searchInput}
                onClear={() => commitSearchNow('')}
                clearAriaLabel={clearSearchLabel}
                accessoryEnd={
                  isSearching ? (
                    <Loader2
                      aria-hidden
                      data-testid="search-spinner"
                      className="shrink-0 animate-spin text-text-secondary"
                      size={16}
                    />
                  ) : undefined
                }
                aria-busy={isSearching}
                wrapperClassName={cn('w-full max-w-sm', !authed && 'ms-auto')}
                placeholder={searchPlaceholder}
              />
            </div>
            <div className="flex flex-col gap-8">
              <TypographyH1 className={cn('text-center', !authed && 'hidden')}>
                <FormattedMessage {...communityMessages.heading} />
              </TypographyH1>
              {!authed && (
                <LoggedOutStateTitleHost
                  search={search}
                  specialty={specialty}
                  setSpecialty={setSpecialty}
                  location={location}
                  setLocation={setLocation}
                  specialties={specialties}
                />
              )}
              <Filters />
            </div>
          </div>

          <div className="flex w-full flex-col items-center gap-8">
            {hasZeroResultsWithoutRecommendations && (
              <TemplatesEmpty isAuthenticated={authed} resetFilters={resetFilters} />
            )}
            <TemplateCards templates={data?.templates} isLoading={isLoading} />
            {!isLoading && data && data.total > 0 && (
              <Pagination
                total={data.total}
                pageSize={communityTemplatePageSize}
                page={page}
                setPage={setPage}
              />
            )}
            {data && (
              <RelatedTemplates
                directTemplates={data.templates}
                recommendedTemplates={data.recommended_templates}
                disabledFilters={data.disabled_filters}
                isLoading={isLoading}
                isZeroResults={hasSettledZeroResults}
              />
            )}
            {hasZeroResultsWithRecommendations &&
              data &&
              data.total_recommended_templates > 9 && (
                <Button size="lg" disableAutomaticSvgSizing onClick={() => handleClick()}>
                  <FormattedMessage {...communityMessages.viewAllResults} />
                </Button>
              )}
          </div>
        </PageShell.Container>
      </div>
    </>
  );
};

export default Community;
